import React from 'react';
import styled from 'astroturf/react';

import { Modal } from 'js/design_system/Modal';

export const StyledModal = styled(Modal)`
  overscroll-behavior: contain;

  > :global(.ds2-5-modal__container) {
    align-items: center;
    min-height: min(500px, 100vh);

    @media (min-width: 420px) and (max-width: 567px) {
      padding-left: 40px;
      padding-right: 40px;
    }

    @media (min-width: 568px) and (max-width: 1279px) {
      padding-left: 16px;
      padding-right: 16px;
    }

    @media (min-width: 1280px) {
      padding-left: 40px;
      padding-right: 40px;
    }

    @media (max-width: 567px) {
      border-radius: 0;
      overflow: hidden;
    }

    @media (min-width: 568px) {
      max-height: 90vh;
      overflow: hidden;
      overscroll-behavior: contain;
    }

    @media (max-height: 750px) {
      --modal-padding-top-bottom: 24px;
    }
  }
`;

export const primaryTextStyle: React.CSSProperties = {
  color: 'var(--text-primary, #141413)',
};

export const emptyFilterBodyStyle: React.CSSProperties = {
  color: 'var(--text-secondary, #6b6b69)',
  maxWidth: 280,
};

export const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: var(--text-primary, #141413);
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: flex-start;
  gap: var(--spacing-xs-8, 8px);
  width: 100%;
  padding: var(--spacing-xs-12, 12px) 0;
`;

/** Wrapper that scales heading from sans-serif lg -> md on small screens. */
export const ResponsiveTitleWrap = styled.div`
  h2 {
    margin: 0;
    text-align: left;
    color: var(--text-primary, #222233);
  }

  @media (max-width: 500px) {
    h2 {
      font-size: var(--type-sans-serif-md-size, 24px);
      line-height: var(--type-sans-serif-md-line-height, 32px);
    }
  }
`;

export const ValueProps = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: var(--spacing-xs-16, 16px);
  width: 100%;

  @media (max-width: 567px) {
    justify-content: center;
    gap: var(--spacing-xs-8, 8px);
  }
`;

export const ValueProp = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--text-primary, #141413);

  &.hideOnMobile {
    @media (max-width: 567px) {
      display: none;
    }
  }

  @media (max-width: 567px) {
    :global(.ds2-5-body-sm) {
      font-size: var(--type-body-xs-size, 12px);
      line-height: var(--type-body-xs-line-height, 16px);
    }
  }
`;

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  flex-shrink: 0;
  background: var(--stroke-grey-light, #dddde2);
`;

export const ModalBody = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  gap: var(--spacing-xs-16, 16px);
  padding-top: var(--spacing-xs-16, 16px);
  flex: 1;
  min-height: 0;

  @media (max-width: 567px) {
    grid-template-rows: auto 1fr;

    /* When filters are expanded on mobile, switch to flex so FiltersColumn
       can grow to fill remaining height (hiding PreviewColumn and Footer). */
    &.expanded {
      display: flex;
      flex-direction: column;
      gap: 0;
    }
  }

  @media (min-width: 568px) {
    grid-template-columns: auto 1fr;
    grid-template-rows: minmax(0, 1fr);
    row-gap: 0;
    column-gap: 24px;
    align-items: start;
    justify-content: center;
    overflow: hidden;
    overscroll-behavior: contain;

    &.collapsed {
      grid-template-columns: 1fr;
    }
  }
`;

export const FiltersColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  gap: var(--spacing-sm-24, 24px);
  padding-top: var(--spacing-xs-8, 8px);

  /* Mobile: full-width accordion card */
  width: 100%;

  @media (max-width: 567px) {
    background: var(--background-light-blue, #eef2f9);
    border-radius: var(--corner-radius-radius-lg, 16px);
    overflow: hidden;
    gap: 0;
    padding-top: 0;

    /* Expanded: grow to fill all available space in the flex Body */
    &.expanded {
      flex: 1;
      min-height: 0;
    }
  }

  /* Tablet+: fixed sidebar width */
  @media (min-width: 568px) {
    width: 164px;
    min-width: 164px;
    min-height: 0;
    padding-left: 2px;
    padding-bottom: var(--spacing-xs-16, 16px);
    overflow: visible;
  }

  /* Large desktop: wider sidebar */
  @media (min-width: 1280px) {
    width: 212px;
    min-width: 212px;
  }
`;

/** Divider between the accordion header and expanded content on mobile. */
export const MobileAccordionDivider = styled.div`
  display: none;

  @media (max-width: 567px) {
    display: block;
    width: 100%;
    height: 1px;
    background: var(--background-beige-med, #f2f0ec);
    flex-shrink: 0;
  }
`;

export const PreviewColumn = styled.div`
  width: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media (min-width: 568px) {
    align-self: stretch;
  }
`;

export const DesktopCustomizeLink = styled.button`
  display: none;

  @media (min-width: 568px) {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    gap: var(--spacing-xs-8, 8px);
    padding: 0;
    margin-bottom: var(--spacing-xs-8, 8px);
    background: transparent;
    border: 0;
    cursor: pointer;
    color: var(--text-link, #0645b1);
    /* Layer above the later-painted PreviewContainer so the icon isn't overdrawn */
    position: relative;
    z-index: 1;

    /* Animate in when filters collapse */
    animation: customizeLinkFadeIn 0.25s ease-out;
  }

  @media (min-width: 1280px) {
    margin-bottom: var(--spacing-sm-24, 24px);
  }

  @keyframes customizeLinkFadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const DesktopCustomizeIconWrap = styled.span`
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: var(--corner-radius-radius-sm, 4px);
  background: var(--background-light-blue, #eef2f9);
  color: var(--text-link, #0645b1);
`;

export const PreviewContainer = styled.div`
  width: 100%;
  background: var(--background-light-blue, #eef2f9);
  border-radius: var(--corner-radius-radius-lg, 16px);
  padding: var(--spacing-xs-16, 16px);
  position: relative;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 1279px) {
    padding: 10px 0 10px 6px;
    --card-h-padding: 8px;
  }
`;

export const PaperContentArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/** Wraps preview content so flex chain reaches WorksScroll */
export const PreviewContentFlex = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const LoadingState = styled.div`
  height: 552px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  justify-content: center;
  align-items: center;

  @media (max-width: 567px) {
    height: auto;
    min-height: 120px;
    flex: 1;
  }
`;

export const EmptyFilterState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  gap: 8px;
`;

export const EmptyFilterIcon = styled.div`
  color: var(--text-tertiary, #9b9a97);
  margin-bottom: 4px;
`;

export const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 999px;
  border: 4px solid var();
  border-top-color: var(--primary-brand, #0645b1);
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Footer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column-reverse;
  flex-shrink: 0;
  gap: var(--spacing-sm-24, 24px);
  align-items: center;
  padding-top: var(--spacing-sm-24, 24px);

  @media (min-width: 568px) and (max-width: 1279px) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-sm-24, 24px);
    padding: var(--spacing-xs-16, 16px) 0;
    border-top: 1px solid var(--stroke-grey-light, #dddde2);
    position: relative;
    z-index: 2;
    background: white;
  }

  @media (min-width: 1280px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-end;
    position: relative;
    z-index: 2;
    background: white;
  }

  /* Premium variant: center the button when no secondary action */
  &.premium {
    @media (min-width: 568px) {
      justify-content: center;
    }
  }
`;

export const NoThanksButton = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: var(--text-primary, #141413);

  @media (min-width: 568px) {
    display: flex;
    align-items: center;
  }

  @media (min-width: 1280px) {
    height: 64px;
  }
`;

export const PrimaryButtonWrap = styled.div`
  width: 100%;

  @media (min-width: 568px) {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: auto;
  }
`;
